import React, { useEffect } from 'react';
import { WhatsAppIcon, PhoneCallIcon } from './icons';
import {
  PHONE,
  PHONE_DISPLAY,
  waUrl,
  waMessages,
  reportConversion,
} from '../utils/leads';

const steps = [
  ['Tell us your idea', 'A few lines on WhatsApp about your business and what you want — that’s enough to start.'],
  ['Get a free plan & fixed quote', 'Within 24 hours you get a clear scope, price and timeline. No obligation.'],
  ['Watch it get built', 'Demo links and regular updates while we work. You pay as milestones are delivered.'],
];

const Contact: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const ctaUrl = waUrl(waMessages.quote);

  return (
    <div className="min-h-screen bg-ink-950 text-white">
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] glow-ember pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <span className="eyebrow justify-center">Contact</span>
          <h1 className="font-display font-bold text-4xl md:text-6xl tracking-tight mt-5 mb-6 leading-[1.05]">
            One message away from{' '}
            <span className="text-gradient">a free quote.</span>
          </h1>
          <p className="text-lg md:text-xl text-white/65 leading-relaxed max-w-2xl mx-auto">
            WhatsApp is fastest — we usually reply within minutes during the
            day. Calls and email work too.
          </p>
        </div>
      </section>

      {/* Channels */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-5">
            {/* WhatsApp — primary */}
            <a
              href={ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => reportConversion(ctaUrl)}
              className="glass p-8 text-center hover:border-whatsapp/60 transition-colors group"
            >
              <div className="w-16 h-16 mx-auto rounded-2xl bg-whatsapp/15 text-whatsapp flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <WhatsAppIcon className="w-8 h-8" />
              </div>
              <h3 className="font-display font-bold text-xl mb-2">WhatsApp</h3>
              <p className="text-white/55 text-sm mb-5">
                Fastest reply — quotes usually within hours.
              </p>
              <span className="text-whatsapp font-semibold text-sm">
                Start chatting →
              </span>
            </a>

            {/* Call */}
            <a
              href={`tel:${PHONE}`}
              className="glass p-8 text-center hover:border-ember/60 transition-colors group"
            >
              <div className="w-16 h-16 mx-auto rounded-2xl bg-ember/15 text-ember flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <PhoneCallIcon className="w-8 h-8" />
              </div>
              <h3 className="font-display font-bold text-xl mb-2">Call us</h3>
              <p className="text-white/55 text-sm mb-5">
                Prefer talking? We speak Hindi and English.
              </p>
              <span className="text-ember font-semibold text-sm">
                {PHONE_DISPLAY}
              </span>
            </a>

            {/* Email */}
            <a
              href="mailto:hello@jsonplay.com"
              className="glass p-8 text-center hover:border-white/40 transition-colors group"
            >
              <div className="w-16 h-16 mx-auto rounded-2xl bg-white/10 text-white flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
              </div>
              <h3 className="font-display font-bold text-xl mb-2">Email</h3>
              <p className="text-white/55 text-sm mb-5">
                Good for documents and detailed briefs.
              </p>
              <span className="text-white/80 font-semibold text-sm">
                hello@jsonplay.com
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* What happens next */}
      <section className="py-16 md:py-20 bg-ink-900/40 border-y border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-10 text-center">
            What happens after you message us
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {steps.map(([title, text], i) => (
              <div key={title} className="glass p-7">
                <div className="w-10 h-10 rounded-full bg-ember/15 text-ember font-display font-bold flex items-center justify-center mb-4">
                  {i + 1}
                </div>
                <h3 className="font-display font-bold text-lg mb-2">{title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-4">
            Your competitors are already online.
          </h2>
          <p className="text-white/60 text-lg mb-8">
            Send the first message today — it costs nothing.
          </p>
          <a
            href={ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp"
            onClick={() => reportConversion(ctaUrl)}
          >
            <WhatsAppIcon className="w-5 h-5" />
            Get my free quote
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
